import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { v4 } from "uuid";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { MetodoPagamento } from "../../../core/enum/MetodoPagamento";
import { TipoAnexoPedido } from "../../enum/TipoAnexoPedido";
import { TipoPedido } from "../../enum/TipoPedido";
import { usePedidos } from "../../hooks/usePedidos";
import { IPedido } from "../../interface/IPedido";
import { IProdutoPedido } from "../../interface/IProdutoPedido";
import { IConfiguracaoPedidos } from "../../interface/IConfiguracaoPedidos";
import { useConfiguracoesPedidoContext } from "../../contexts/ConfiguracoesPedidoContext";
import { TipoProduto } from "../../../produtos/enum/TipoProduto";
import { StatusPedidoDescricao } from "../../enum/StatusPedido";
import { IEndereco } from "../../../core/interfaces/IEndereco";
import { ICliente } from "../../../clientes/interface/ICliente";
import moment from "moment";
import { TipoItemProduto } from "../../../produtos/enum/TipoItemProduto";
import { EmitirNotaFiscalModal } from "../../pages/Operacao/containers/EmitirNotaFiscalModal";
import { IDadosEmitirNotaFiscal } from "../../interface/IDadosEmitirNotaFiscal";
import { validarCpf } from "../../../core/utils/validarCpf";
import { validarCnpj } from "../../../core/utils/validarCnpj";
import { IFluxoContexto } from "../../interface/IFluxoContexto";
import { IConfiguracaoComanda } from "../../interface/IConfiguracaoComanda";
import { TipoGatilhoComanda } from "../../enum/TipoGatilhoComanda";
import { TipoMarketplace } from "../../../marketplace/enum/TipoMarketplace";
import { IPagamentoPedido } from "../../interface/IPagamentoPedido";
import { useEntregas } from "../../hooks/useEntregas";
import { TipoCobrancaEntrega } from "../../enum/TipoCobrancaEntrega";
import { useAutenticacaoContext } from "../../../core/contexts/AutenticacaoContext";
import { useProdutos } from "../../../produtos/hooks/useProdutos";
import { DividirPagamento } from "../../pages/Operacao/containers/DividirPagamento";
import { ICupom } from "../../interface/ICupom";
import { calcularValoresComCupons } from "../../utils/calcularValoresComCupons";
import { useCuponsPedido } from "../../hooks/useCuponsPedido";
import { IContatoMensagens } from "../../interface/atendimento/IContatoMensagens";
import { useClientes } from "../../../clientes/hooks/useClientes";
import { useMensagens } from "../../hooks/atendimento/useMensagens";
import { useMascara } from "../../../core/hooks/useMascara";
import { IProduto } from "../../../produtos/interfaces/IProduto";
import { cleanExpiredStates, clearState, loadState, saveState } from "../../hooks/atendimento/useChatPdVPersistence";

export type TelaChatPdV = 'checkout' | 'catalogo' | 'pizza' | 'preparado' | 'peso';

interface IChatPdVContextProps {
    agendadoPara: moment.Moment | null;
    agendarHorario: (hora: moment.Moment | null) => void;
    configuracao: IConfiguracaoPedidos | null | undefined;
    cliente: ICliente | undefined;
    produtos: IProdutoPedido[];
    valorTotalProdutos: number;
    valorTotalPedido: number;
    valorDescontoTotal: number;
    pagamentos: IPagamentoPedido[];
    alterarPagamentos: (pagamentos?: IPagamentoPedido[] | undefined) => void;
    adicionarProduto: (produto: IProdutoPedido) => void;
    adicionarProdutosCombo: (produtos: IProdutoPedido[]) => void;
    editarProduto: (produto: IProdutoPedido) => void;
    aumentarQuantidade: (produtoUuid: string) => Promise<void>;
    diminuirQuantidade: (produtoUuid: string) => Promise<void>;
    removerProduto: (produtoUuid: string) => Promise<void>;
    fecharPedido: (dados?: IDadosEmitirNotaFiscal, pagamentos?: IPagamentoPedido[]) => Promise<void>;
    salvarPedido: () => void;
    loading: boolean;
    tipo: TipoPedido;
    alterarTipo: (tipo: TipoPedido) => void;
    valorTroco: number | undefined;
    valorRecebido: number | undefined;
    valorFrete: number;
    alterarValorFrete: (valor: number) => void;
    alterarEndereco: () => void;
    removerCliente: () => void;
    vincularEndereco: (endereco: IEndereco) => void;
    endereco: IEndereco | undefined;
    vincularCliente: (cliente: ICliente) => void;
    vincularClienteDoChat: (cliente: ICliente) => void;
    resetarTudo: () => void;
    numeroTotalItens: number;
    switchNotaFiscal: boolean;
    switchImprimirAutomatico: boolean;
    handleSwitchImprimirAutomatico: () => void;
    modalNotaFiscal: boolean;
    handleModalNotaFiscal: () => void;
    handleSwitchNotaFiscal: () => void;
    selecionarProduto: (produto: IProdutoPedido) => void;
    produtosSelecionados: IProdutoPedido[];
    fluxoContexto?: IFluxoContexto;
    configuracaoNFCe?: IConfiguracaoComanda;
    loadingVincularEndereco: boolean;
    drawerPagamentoAberto: boolean;
    abrirDrawerPagamento: () => void;
    fecharDrawerPagamento: () => void;
    pedidosEmAndamento: IPedido[];
    loadingPedidosEmAndamento: boolean;
    buscarPedidosEmAndamento: (clienteUuid?: string) => Promise<void>;
    exibirNovoPedido: boolean;
    setExibirNovoPedido: (exibir: boolean) => void;
    cupons: ICupom[];
    aplicarCupom: (cupom: ICupom) => Promise<void>;
    removerCupom: (codigo: string, tipo: string) => Promise<void>;
    drawerCuponsAberto: boolean;
    abrirDrawerCupons: () => void;
    fecharDrawerCupons: () => void;
    contatoChat: IContatoMensagens | undefined;
    clienteIdentificado: ICliente | undefined;
    chatUuid: string | undefined;
    loadingCliente: boolean;
    definirContatoChat: (contato: IContatoMensagens | undefined, chatUuid: string | undefined, clienteUuid?: string) => void;
    vincularClienteNoChat: (cliente: ICliente) => Promise<void>;
    desvincularClienteDoChat: () => Promise<void>;
    cadastrarEVincularCliente: (nome: string, telefone: string) => Promise<void>;
    telaAtual: TelaChatPdV;
    produtoFormulario: IProduto | IProdutoPedido | undefined;
    navegarParaCatalogo: () => void;
    navegarParaFormPizza: (produto: IProduto | IProdutoPedido, origem?: 'catalogo' | 'checkout') => void;
    navegarParaFormPreparado: (produto: IProduto | IProdutoPedido, origem?: 'catalogo' | 'checkout') => void;
    navegarParaFormPeso: (produto: IProduto | IProdutoPedido, origem?: 'catalogo' | 'checkout') => void;
    voltarTela: () => void;
}

export const useChatPdVContext = () => {
    return useContext(ChatPdVContext);
}

export const ChatPdVContext = createContext({} as IChatPdVContextProps);

export const ChatPdVProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const snackbar = useSnackbar();
    const { configuracao } = useConfiguracoesPedidoContext();
    const [tipo, setTipo] = useState<TipoPedido>(TipoPedido.delivery);

    const [pagamentos, setPagamentos] = useState<IPagamentoPedido[]>([]);
    const [produtos, setProdutos] = useState<IProdutoPedido[]>([]);
    const [endereco, setEndereco] = useState<IEndereco | undefined>(undefined);
    const [cliente, setCliente] = useState<ICliente | undefined>(undefined);
    const [agendadoPara, setAgendadoPara] = useState<moment.Moment | null>(null);
    const [switchNotaFiscal, setSwitchNotaFiscal] = useState(false);
    const [switchImprimirAutomatico, setSwitchImprimirAutomatico] = useState(true);
    const [modalNotaFiscal, setModalNotaFiscal] = useState(false);
    const [produtosSelecionados, setProdutosSelecionados] = useState<IProdutoPedido[]>([]);
    const [loadingVincularEndereco, setLoadingVincularEndereco] = useState(false);
    const [pedidosEmAndamento, setPedidosEmAndamento] = useState<IPedido[]>([]);
    const [loadingPedidosEmAndamento, setLoadingPedidosEmAndamento] = useState(false);
    const [exibirNovoPedido, setExibirNovoPedido] = useState(false);
    const { tratarProduto } = useProdutos();
    const [drawerPagamentoAberto, setDrawerPagamento] = useState(false);
    const [cupons, setCupons] = useState<ICupom[]>([]);
    const [drawerCuponsAberto, setDrawerCupons] = useState(false);
    const { aplicarCupom: aplicarCupomAPI, removerCupom: removerCupomAPI } = useCuponsPedido();
    const [contatoChat, setContatoChat] = useState<IContatoMensagens | undefined>(undefined);
    const [clienteIdentificado, setClienteIdentificado] = useState<ICliente | undefined>(undefined);
    const [chatUuid, setChatUuid] = useState<string | undefined>(undefined);
    const [loadingCliente, setLoadingCliente] = useState(false);
    const { buscarClientesPorNomeOuTelefone, buscarPorUuid, salvar: salvarCliente } = useClientes();
    const { vincularCliente: vincularClienteAPI, desvincularCliente: desvincularClienteAPI } = useMensagens();
    const { formatarTelefoneParaPadrao } = useMascara();
    const [telaAtual, setTelaAtual] = useState<TelaChatPdV>('checkout');
    const [telaAnterior, setTelaAnterior] = useState<'checkout' | 'catalogo'>('catalogo');
    const [produtoFormulario, setProdutoFormulario] = useState<IProduto | IProdutoPedido | undefined>(undefined);
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    /**
     * Vínculo do cliente conhecido para o chat aberto. É um ref, e não o state
     * `cliente`, porque o auto-save roda com debounce e leria um `cliente` ainda
     * vazio logo depois de trocar de chat — gravando um snapshot que apagava o
     * vínculo. `undefined` = indefinido, `null` = desvinculado pelo operador.
     */
    const vinculoClienteRef = useRef<string | null | undefined>(undefined);

    const abrirDrawerPagamento = () => {
        setDrawerPagamento(true);
    };

    const fecharDrawerPagamento = () => {
        setDrawerPagamento(false);
    };

    const abrirDrawerCupons = () => {
        setDrawerCupons(true);
    };

    const fecharDrawerCupons = () => {
        setDrawerCupons(false);
    };

    const handleModalNotaFiscal = () => {
        setModalNotaFiscal(!modalNotaFiscal);
    };

    const handleSwitchNotaFiscal = () => {
        setSwitchNotaFiscal(!switchNotaFiscal);
    };

    const handleSwitchImprimirAutomatico = () => {
        setSwitchImprimirAutomatico(!switchImprimirAutomatico);
    };

    const valorFrete = useMemo(
        () => (endereco?.regiao?.preco ?? 0),
        [endereco]
    );

    const { estabelecimento } = useAutenticacaoContext();

    const {
        gerarPedido,
        loading,
        gerarAnexo: gerarAnexoPedido,
        emitirNotaFiscal: emitirNotaFiscalConsumidor,
        listarPedidos,
    } = usePedidos();

    const { calcularFrete } = useEntregas();

    const alterarTipo = (novoTipo: TipoPedido) => {
        setTipo(novoTipo);
        setEndereco(undefined);
    };

    const resetarProdutos = () => {
        setProdutos([]);
    };

    const resetarCheckout = () => {
        setAgendadoPara(null);
        setPagamentos([]);
        setEndereco(undefined);
    };

    const resetarTudo = () => {
        resetarProdutos();
        resetarCheckout();
    };

    const editarProduto = async (produto: IProdutoPedido) => {
        await removerProduto(produto.uuid);
        await adicionarProduto(produto);
    };

    const valorTotalProdutos = useMemo(() => {
        let totalProdutos = 0;

        produtos.map(produto => {
            let totalProduto = (produto.preco ?? 0);

            produto.itens?.map((item: any) => {
                totalProduto = totalProduto + ((item.preco ?? 0) * (item.quantidade ?? 1));
            });

            totalProdutos = totalProdutos + (totalProduto * (produto.quantidade ?? 1));
        });

        return totalProdutos;
    }, [produtos]);

    const valoresCalculados = useMemo(() => {
        const valores = calcularValoresComCupons(
            {
                valorTotalProdutos: valorTotalProdutos,
                valorFrete: valorFrete,
            },
            cupons
        );
        return valores;
    }, [valorTotalProdutos, valorFrete, cupons]);

    const valorTotalPedido = useMemo(() => {
        return valoresCalculados.valorTotalPedido;
    }, [valoresCalculados]);

    const valorDescontoTotal = useMemo(() => {
        return valoresCalculados.valorDescontoTotal;
    }, [valoresCalculados]);

    const numeroTotalItens = useMemo(() => {
        let total = 0;

        produtos.map(produto => {
            total += produto.quantidade ?? 1;
        });

        return total;
    }, [produtos]);

    const valorRecebido = useMemo(() => {
        let total = 0;

        const pagamentosDinheiro = pagamentos?.filter(pagamento => pagamento.metodoPagamento == MetodoPagamento.Dinheiro) ?? [];
        pagamentosDinheiro?.map(pagamento => {
            total = total + (pagamento.valorRecebido ?? 0);
        });

        return total;
    }, [pagamentos]);

    const valorTroco = useMemo(() => valorRecebido ? (valorRecebido - valorTotalPedido) : 0, [valorTotalPedido, valorRecebido]);

    const fluxoContexto = useMemo(() => {
        return configuracao?.fluxoOperacao?.fluxosContextos?.find((fluxo: IFluxoContexto) => fluxo.tipo === tipo);
    }, [configuracao, tipo]);

    const configuracaoNFCe = useMemo(() => {
        return fluxoContexto?.configuracoesComandas.find((config: IConfiguracaoComanda) => config.tipo == TipoAnexoPedido.nota_fiscal);
    }, [fluxoContexto]);

    useEffect(() => {
        if (configuracaoNFCe?.obrigatorio == true) {
            setSwitchNotaFiscal(true);
        } else {
            setSwitchNotaFiscal(false);
        }
    }, [fluxoContexto, configuracaoNFCe]);

    // Limpa entradas expiradas do localStorage na inicialização
    useEffect(() => {
        cleanExpiredStates();
    }, []);

    // Auto-save debounced: salva o estado do chat atual sempre que algo relevante mudar
    useEffect(() => {
        if (!chatUuid) return;

        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }

        autoSaveTimerRef.current = setTimeout(() => {
            saveState(chatUuid, {
                tipo,
                produtos,
                pagamentos,
                endereco,
                agendadoPara: agendadoPara?.format("YYYY-MM-DDTHH:mm") ?? null,
                cupons,
                switchNotaFiscal,
                switchImprimirAutomatico,
                clienteUuid: vinculoClienteRef.current,
            });
        }, 500);

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [chatUuid, tipo, produtos, pagamentos, endereco, agendadoPara, cupons, switchNotaFiscal, switchImprimirAutomatico]);

    const alterarPagamentos = async (pagamentos?: IPagamentoPedido[]) => {
        if (!pagamentos) {
            pagamentos = [];
        }

        setPagamentos(pagamentos);
    };

    const adicionarProdutosCombo = (novosProdutos: IProdutoPedido[]) => {
        if (pagamentos.length) {
            alterarPagamentos([]);
        }
        setProdutos(prev => [...prev, ...novosProdutos]);
    };

    const adicionarProduto = async (produto: IProdutoPedido) => {
        if (pagamentos.length) {
            await alterarPagamentos([]);
            snackbar("O pagamento foi removido", { severity: "info" });
        }

        // Remover cupons quando produtos mudarem
        if (cupons.length > 0) {
            setCupons([]);
            snackbar("Cupons removidos devido à alteração nos produtos", { severity: "info" });
        }

        const produtoJaExiste = produtos.find(produtoCarrinho =>
            produtoCarrinho.uuidProdutoReferencia === produto.uuidProdutoReferencia &&
            !produtoCarrinho.comboUuid
        );

        if (
            produtoJaExiste &&
            produtoJaExiste.uuid &&
            produto.tipo === TipoProduto.industrializado
        ) {
            return aumentarQuantidade(produtoJaExiste.uuid);
        }

        if (
            produtoJaExiste &&
            produtoJaExiste.uuid &&
            produto.tipo === TipoProduto.preparado &&
            (
                produto.configuracao?.habilitarComplementos === false ||
                produto.configuracao?.habilitarObservacao === false
            )
        ) {
            return aumentarQuantidade(produtoJaExiste.uuid);
        }

        const produtoTratado = tratarProduto(produto);
        if (!produtoTratado) {
            return snackbar("Produto invalido", { severity: "error" });
        }
        // Garantir que o preço está preservado
        if (!produtoTratado.preco && produto.preco) {
            produtoTratado.preco = produto.preco;
        }
        setProdutos((produtosPedido) => [...produtosPedido, produtoTratado]);
        snackbar(`${produto.descricao} adicionado ao carrinho`, { severity: "success" });

        return;
    };

    const salvarPedido = async () => {
        const dados = {
            status: StatusPedidoDescricao["Em andamento"]
        } as IPedido;

        const pedido = await abrirPedido(dados, true);
        if (pedido) {
            snackbar("Pedido salvo com sucesso", { severity: "success" });
        }
    };

    const limparPedido = () => {
        setProdutosSelecionados([]);
        setProdutos([]);
        setPagamentos([]);
        setEndereco(undefined);
        setAgendadoPara(null);
        setCupons([]);
    };

    const aplicarCupom = async (cupom: ICupom) => {
        // Aplicar localmente (não há pedidoUuid em novo pedido)
        const sucesso = await aplicarCupomAPI(undefined, cupom);
        if (sucesso) {
            setCupons((prev) => {
                // Verificar se já existe antes de adicionar
                const jaExiste = prev.some(
                    (c) => c.codigo === cupom.codigo && c.tipo === cupom.tipo
                );
                if (jaExiste) {
                    snackbar("Este cupom já foi aplicado", { severity: "warning" });
                    return prev;
                }
                return [...prev, cupom];
            });
        }
    };

    const removerCupom = async (codigo: string, tipo: string) => {
        // Remover localmente (não há pedidoUuid em novo pedido)
        const sucesso = await removerCupomAPI(undefined, codigo, tipo as any);
        if (sucesso) {
            setCupons((prev) => {
                return prev.filter((c) => !(c.codigo === codigo && c.tipo === tipo));
            });
        }
    };

    const abrirPedido = async (dadosPedido?: IPedido, limpar?: boolean) => {
        const produtosPedido = dadosPedido?.produtos ? dadosPedido?.produtos?.map((produto: IProdutoPedido) => {
            return tratarProduto(produto);
        }) : produtos;

        const dadosTratadosPedido = {
            tipo: dadosPedido?.tipo ?? tipo,
            produtos: produtosPedido,
            pagamentos: pagamentos,
            cliente: dadosPedido?.cliente ?? cliente,
            endereco: dadosPedido?.endereco ?? endereco,
            agendadoPara: dadosPedido?.agendadoPara ?? agendadoPara?.format("YYYY-MM-DDTHH:mm"),
            status: dadosPedido?.status,
            cupons: cupons.length > 0 ? cupons : undefined,
            marketplace: {
                tipo: TipoMarketplace.PDV
            }
        } as IPedido;

        const { pedido } = await gerarPedido(dadosTratadosPedido, tipo);

        if (pedido && limpar) {
            setTimeout(() => limparPedido(), 1000);
        }

        return pedido;
    };

    const fecharPedido = async (dadosEmitirNota?: IDadosEmitirNotaFiscal, pagamentosParametro?: IPagamentoPedido[]) => {
        if (switchNotaFiscal && !modalNotaFiscal) {
            return handleModalNotaFiscal();
        }

        if (dadosEmitirNota && switchNotaFiscal) {
            if (dadosEmitirNota.tipoPessoa == "CPF" && !validarCpf(dadosEmitirNota.numeroPessoa, true)) {
                return snackbar("CPF inválido", { severity: "error" });
            }

            if (dadosEmitirNota.tipoPessoa == "CNPJ" && !validarCnpj(dadosEmitirNota.numeroPessoa, true)) {
                return snackbar("CNPJ inválido", { severity: "error" });
            }
        }

        const fluxo = fluxoContexto?.fluxo;
        if (!produtos.length) {
            return snackbar("Nenhum produto adicionado ao pedido", { severity: "error" });
        }

        // Validações antes de fechar o pedido
        if (tipo === TipoPedido.delivery) {
            if (!cliente) {
                return snackbar("Cliente indefinido", { severity: "error" });
            }
            if (!endereco) {
                return snackbar("Endereço indefinido", { severity: "error" });
            }
        }

        if (tipo === TipoPedido.retirada) {
            if (!cliente) {
                return snackbar("Cliente indefinido", { severity: "error" });
            }
        }

        const dadosPedido = {
            agendadoPara: agendadoPara?.format("YYYY-MM-DDTHH:mm"),
            produtos: produtos,
            endereco,
            cliente,
            tipo,
            status: fluxo == "PDV" ? "Concluído" : "Em preparo",
            pagamentos: pagamentosParametro ?? pagamentos,
            cupons: cupons.length > 0 ? cupons : undefined,
            marketplace: {
                tipo: TipoMarketplace.PDV
            }
        } as IPedido;

        try {
            const resultado = await gerarPedido(dadosPedido, tipo);
            if (!resultado) {
                snackbar("Ocorreu um erro ao fechar o pedido", { severity: "error" });
                return;
            }

            // gerarPedido retorna { pedido } ou o pedido diretamente
            const pedidoGerado = resultado.pedido ?? resultado;
            
            if (switchImprimirAutomatico && pedidoGerado) {
                gerarAnexosBotaoFecharPedido(pedidoGerado, dadosEmitirNota);
            }

            snackbar("Pedido fechado com sucesso", { severity: "success" });
            setProdutos([]);
            setPagamentos([]);
            setEndereco(undefined);
            setAgendadoPara(null);
            setCupons([]);
            if (chatUuid) clearState(chatUuid);

            // Retornar para a listagem de pedidos em andamento
            setExibirNovoPedido(false);
            if (cliente?.uuid) {
                await buscarPedidosEmAndamento(cliente.uuid);
            }
        } catch (error: any) {
            const mensagemErro = error?.response?.data?.message || error?.message || "Ocorreu um erro ao fechar o pedido";
            snackbar(mensagemErro, { severity: "error" });
        }
    };

    const gerarAnexosBotaoFecharPedido = async (pedido: IPedido, dados?: IDadosEmitirNotaFiscal) => {
        if (switchNotaFiscal == true) {
            setTimeout(async () => await emitirNotaFiscalConsumidor(pedido.uuid, dados), 1000);
            handleModalNotaFiscal();
        }

        const configuracoesComanda = fluxoContexto?.configuracoesComandas.filter((config: IConfiguracaoComanda) => {
            if (config.gatilhos.includes(TipoGatilhoComanda.BOTAO_CONCLUIR) && config.obrigatorio && config.imprimirAutomaticamente !== false) {
                return config;
            }
        });

        const configuracoesNaoFiscais = configuracoesComanda?.filter((config: IConfiguracaoComanda) => config.tipo != TipoAnexoPedido.nota_fiscal);
        configuracoesNaoFiscais?.forEach((config: IConfiguracaoComanda, index: number) => {
            setTimeout(async () => {
                await gerarAnexoPedido(pedido.uuid, config.tipo, config.uuid);
            }, 1000 * (index + 1));
        });
    };

    const aumentarQuantidade = async (uuid: string) => {
        if (pagamentos.length) {
            await alterarPagamentos([]);
            snackbar("O pagamento foi removido", { severity: "info" });
        }

        if (!produtos || !produtos.length) {
            return snackbar("Nenhum produto no carrinho", { severity: "error" });
        }

        const index = produtos.findIndex(produto => produto.uuid === uuid);

        if (index === -1) {
            return snackbar("Produto não encontrado", { severity: "error" });
        }

        // Remover cupons quando produtos mudarem
        if (cupons.length > 0) {
            setCupons([]);
            snackbar("Cupons removidos devido à alteração nos produtos", { severity: "info" });
        }

        produtos[index].quantidade = (produtos[index].quantidade ?? 0) + 1;
        setProdutos([...produtos]);
    };

    const diminuirQuantidade = async (uuid: string) => {
        if (pagamentos.length) {
            await alterarPagamentos([]);
            snackbar("O pagamento foi removido", { severity: "info" });
        }

        if (!produtos || !produtos.length) {
            return snackbar("Nenhum produto no carrinho", { severity: "error" });
        }

        const index = produtos.findIndex(produto => produto.uuid === uuid);

        if (index === -1) {
            return snackbar("Produto não encontrado", { severity: "error" });
        }

        // Remover cupons quando produtos mudarem
        if (cupons.length > 0) {
            setCupons([]);
            snackbar("Cupons removidos devido à alteração nos produtos", { severity: "info" });
        }

        produtos[index].quantidade = (produtos[index].quantidade ?? 0) - 1;
        setProdutos([...produtos]);

        if (!produtos[index].quantidade) {
            removerProduto(uuid);
        }
    };

    const removerProduto = async (uuid: string) => {
        if (pagamentos.length) {
            await alterarPagamentos([]);
            snackbar("O pagamento foi removido", { severity: "info" });
        }

        const index = produtos.findIndex(produto => produto.uuid === uuid);

        if (index === -1) {
            return snackbar("Não foi possível remover o produto", { severity: "error" });
        }

        // Remover cupons quando produtos mudarem
        if (cupons.length > 0) {
            setCupons([]);
            snackbar("Cupons removidos devido à alteração nos produtos", { severity: "info" });
        }

        produtos.splice(index, 1);
        const produtosAtualizados = [...produtos];
        setProdutos(produtosAtualizados);
    };

    const agendarHorario = (hora: moment.Moment | null) => {
        if (hora) {
            setAgendadoPara(hora);
        }
    };

    const alterarEndereco = () => {
        // Remover cupons quando endereço mudar
        if (cupons.length > 0) {
            setCupons([]);
            snackbar("Cupons removidos devido à alteração no endereço", { severity: "info" });
        }
        setEndereco(undefined);
    };

    const removerCliente = () => {
        setEndereco(undefined);
        // Não remove o cliente no chat, apenas o endereço
    };

    const vincularCliente = (cliente: ICliente) => {
        setCliente(cliente);
    };

    const vincularClienteDoChat = (cliente: ICliente) => {
        setCliente(cliente);
        setExibirNovoPedido(false);
    };

    const buscarPedidosEmAndamento = async (clienteUuidParam?: string) => {
        const uuidCliente = clienteUuidParam || cliente?.uuid;
        if (!uuidCliente) {
            setPedidosEmAndamento([]);
            return;
        }

        setLoadingPedidosEmAndamento(true);
        try {
            const statusEmAndamento = [
                StatusPedidoDescricao["Em preparo"],
                StatusPedidoDescricao["Em andamento"],
                StatusPedidoDescricao["Em transporte"],
                StatusPedidoDescricao["Aguardando confirmação"],
                StatusPedidoDescricao["Aguardando pagamento"],
                StatusPedidoDescricao["Pronto para retirada"],
                StatusPedidoDescricao["Em negociação"],
            ].join(',');

            const pedidos = await listarPedidos({
                clienteUuid: uuidCliente,
                status: statusEmAndamento,
            });

            if (pedidos && Array.isArray(pedidos)) {
                setPedidosEmAndamento(pedidos);
            } else {
                setPedidosEmAndamento([]);
            }
        } catch (error) {
            console.error('Erro ao buscar pedidos em andamento:', error);
            setPedidosEmAndamento([]);
        } finally {
            setLoadingPedidosEmAndamento(false);
        }
    };

    const vincularEndereco = async (props: IEndereco) => {
        let regiao = props?.regiao;

        // Sempre recalcula o frete se for por distância, mesmo que o endereço já tenha região
        if (configuracao?.tipoCobrancaEntrega === TipoCobrancaEntrega.DISTANCIA) {
            if (!configuracao?.distanciasEntrega?.length) {
                return snackbar("Nenhuma distância configurada para calcular o frete", { severity: 'error' });
            }

            if (!estabelecimento?.endereco) {
                return snackbar("O endereço do estabelecimento não está configurado", { severity: 'error' });
            }

            setLoadingVincularEndereco(true);

            const regiaoGerada = await calcularFrete({
                enderecoDestino: props,
                distancias: configuracao?.distanciasEntrega,
                enderecoOrigem: estabelecimento?.endereco
            });

            setLoadingVincularEndereco(false);

            if (!regiaoGerada) {
                return;
            }

            regiao = regiaoGerada;
        }

        if (configuracao?.tipoCobrancaEntrega === TipoCobrancaEntrega.REGIAO) {
            if (!regiao) {
                return snackbar("Região não configurada", { severity: "error" });
            }
            const regiaoAtualizada = configuracao?.regioesEntrega?.find(r => r.uuid === regiao!.uuid);
            if (regiaoAtualizada) {
                regiao = { ...regiao, preco: regiaoAtualizada.preco };
            }
        }

        const endereco = {
            ...props,
            regiao
        }
        // Remover cupons quando endereço mudar
        if (cupons.length > 0) {
            setCupons([]);
            snackbar("Cupons removidos devido à alteração no endereço", { severity: "info" });
        }
        setEndereco(endereco);
    };

    const selecionarProduto = (produto: IProdutoPedido) => {
        const produtoIndex = produtosSelecionados.findIndex(produtoProcurado => produtoProcurado.uuid == produto.uuid);

        if (produtoIndex !== -1) {
            const novosProdutos = produtosSelecionados;
            novosProdutos.splice(produtoIndex, 1);
            setProdutosSelecionados([...novosProdutos]);
            return;
        }

        setProdutosSelecionados([...produtosSelecionados, produto]);
    };

    const definirContatoChat = async (contato: IContatoMensagens | undefined, novoChat: string | undefined, clienteUuid?: string) => {
        // Salvar estado do chat atual antes de trocar
        if (chatUuid) {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
                autoSaveTimerRef.current = null;
            }
            saveState(chatUuid, {
                tipo,
                produtos,
                pagamentos,
                endereco,
                agendadoPara: agendadoPara?.format("YYYY-MM-DDTHH:mm") ?? null,
                cupons,
                switchNotaFiscal,
                switchImprimirAutomatico,
                clienteUuid: vinculoClienteRef.current,
            });
        }

        setContatoChat(contato);
        setChatUuid(novoChat);
        setClienteIdentificado(undefined);
        setCliente(undefined);
        vinculoClienteRef.current = undefined;
        setPedidosEmAndamento([]);
        setExibirNovoPedido(false);
        setTelaAtual('checkout');
        setProdutoFormulario(undefined);

        // Restaurar estado do novo chat, se existir e não estiver expirado
        const persisted = novoChat ? loadState(novoChat) : null;
        if (persisted) {
            setTipo(persisted.tipo);
            setProdutos(persisted.produtos);
            setPagamentos(persisted.pagamentos);
            setEndereco(persisted.endereco);
            setAgendadoPara(persisted.agendadoPara ? moment(persisted.agendadoPara) : null);
            setCupons(persisted.cupons);
            setSwitchNotaFiscal(persisted.switchNotaFiscal);
            setSwitchImprimirAutomatico(persisted.switchImprimirAutomatico);
        } else {
            limparPedido();
        }

        if (!contato || !novoChat) return;

        // O vínculo mora no servidor (chat.clienteUuid). O snapshot local só tem
        // prioridade quando registra uma decisão explícita de desvincular —
        // senão um snapshot salvo antes do cliente carregar zerava o vínculo a
        // cada abertura do chat (e a cada F5).
        const uuidPersistido = persisted?.clienteUuid;
        const uuidCliente = uuidPersistido === null
            ? undefined
            : (uuidPersistido ?? clienteUuid ?? contato.uuidCliente);

        // Se já tem um cliente vinculado, buscar dados completos e pular seleção
        if (uuidCliente) {
            setLoadingCliente(true);
            try {
                const clienteCompleto = await buscarPorUuid(uuidCliente);
                if (clienteCompleto) {
                    setCliente(clienteCompleto);
                    vinculoClienteRef.current = clienteCompleto.uuid ?? uuidCliente;
                    await buscarPedidosEmAndamento(uuidCliente);
                    return;
                }
            } catch (error) {
                console.error('Erro ao buscar cliente por UUID:', error);
            } finally {
                setLoadingCliente(false);
            }
        }

        // Buscar cliente pelo telefone (apenas busca, NÃO cria)
        if (contato.telefone) {
            setLoadingCliente(true);
            try {
                const telefoneFormatado = formatarTelefoneParaPadrao(contato.telefone) || contato.telefone;
                const clientesEncontrados = await buscarClientesPorNomeOuTelefone(telefoneFormatado, 'telefone');
                if (Array.isArray(clientesEncontrados) && clientesEncontrados.length > 0) {
                    setClienteIdentificado(clientesEncontrados[0]);
                }
            } catch (error) {
                console.error('Erro ao buscar cliente pelo telefone:', error);
            } finally {
                setLoadingCliente(false);
            }
        }
    };

    const vincularClienteNoChat = async (clienteParaVincular: ICliente) => {
        if (!chatUuid || !clienteParaVincular.uuid) return;

        setLoadingCliente(true);
        try {
            const sucesso = await vincularClienteAPI(chatUuid, clienteParaVincular.uuid);
            if (sucesso) {
                setCliente(clienteParaVincular);
                vinculoClienteRef.current = clienteParaVincular.uuid;
                setExibirNovoPedido(false);
                await buscarPedidosEmAndamento(clienteParaVincular.uuid);
            }
        } finally {
            setLoadingCliente(false);
        }
    };

    const desvincularClienteDoChat = async () => {
        if (!chatUuid) return;

        setLoadingCliente(true);
        try {
            const sucesso = await desvincularClienteAPI(chatUuid);
            if (sucesso) {
                setCliente(undefined);
                vinculoClienteRef.current = null;
                setEndereco(undefined);
                setPedidosEmAndamento([]);
                setExibirNovoPedido(false);
                limparPedido();
                saveState(chatUuid, {
                    tipo,
                    produtos,
                    pagamentos,
                    endereco: undefined,
                    agendadoPara: agendadoPara?.format("YYYY-MM-DDTHH:mm") ?? null,
                    cupons,
                    switchNotaFiscal,
                    switchImprimirAutomatico,
                    clienteUuid: null,
                });
            }
        } finally {
            setLoadingCliente(false);
        }
    };

    const cadastrarEVincularCliente = async (nome: string, telefone: string) => {
        if (!chatUuid) return;

        setLoadingCliente(true);
        try {
            const novoCliente: ICliente = {
                nome: nome.toUpperCase(),
                telefone: formatarTelefoneParaPadrao(telefone) || telefone,
            };

            const resultado = await salvarCliente(novoCliente);
            if (resultado?.cliente?.uuid) {
                await vincularClienteNoChat(resultado.cliente);
            }
        } catch (error) {
            snackbar("Erro ao cadastrar cliente", { severity: "error" });
        } finally {
            setLoadingCliente(false);
        }
    };

    const navegarParaCatalogo = () => setTelaAtual('catalogo');

    const navegarParaFormPizza = (produto: IProduto | IProdutoPedido, origem: 'catalogo' | 'checkout' = 'catalogo') => {
        setTelaAnterior(origem);
        setProdutoFormulario(produto);
        setTelaAtual('pizza');
    };

    const navegarParaFormPreparado = (produto: IProduto | IProdutoPedido, origem: 'catalogo' | 'checkout' = 'catalogo') => {
        setTelaAnterior(origem);
        setProdutoFormulario(produto);
        setTelaAtual('preparado');
    };

    const navegarParaFormPeso = (produto: IProduto | IProdutoPedido, origem: 'catalogo' | 'checkout' = 'catalogo') => {
        setTelaAnterior(origem);
        setProdutoFormulario(produto);
        setTelaAtual('peso');
    };

    const voltarTela = () => {
        if (telaAtual === 'pizza' || telaAtual === 'preparado' || telaAtual === 'peso') {
            setTelaAtual(telaAnterior);
        } else {
            setTelaAtual('checkout');
        }
        setProdutoFormulario(undefined);
    };

    const alterarValorFrete = (valor: number) => {
        // Remover cupons quando valor do frete mudar
        if (cupons.length > 0) {
            setCupons([]);
            snackbar("Cupons removidos devido à alteração no valor do frete", { severity: "info" });
        }
        
        setEndereco((enderecoAtual: IEndereco | undefined) => {
            if (!enderecoAtual || !enderecoAtual.regiao) {
                snackbar("Endereço indefinido", { severity: "error" });
                return enderecoAtual;
            }

            return {
                ...enderecoAtual,
                regiao: {
                    ...enderecoAtual.regiao,
                    preco: valor,
                },
            };
        });

        snackbar("Frete atualizado", { severity: "success" });
    };

    return (
        <ChatPdVContext.Provider value={{
            adicionarProduto,
            adicionarProdutosCombo,
            editarProduto,
            configuracao,
            produtos,
            valorTotalProdutos,
            valorTotalPedido,
            valorDescontoTotal,
            alterarPagamentos,
            pagamentos,
            aumentarQuantidade,
            diminuirQuantidade,
            removerProduto,
            fecharPedido,
            loading,
            alterarTipo,
            tipo,
            valorRecebido,
            valorTroco,
            agendadoPara,
            agendarHorario,
            alterarEndereco,
            cliente,
            endereco,
            removerCliente,
            valorFrete,
            vincularCliente,
            vincularEndereco,
            vincularClienteDoChat,
            alterarValorFrete,
            salvarPedido,
            resetarTudo,
            numeroTotalItens,
            handleModalNotaFiscal,
            handleSwitchNotaFiscal,
            modalNotaFiscal,
            switchNotaFiscal,
            selecionarProduto,
            produtosSelecionados,
            fluxoContexto,
            configuracaoNFCe,
            loadingVincularEndereco,
            handleSwitchImprimirAutomatico,
            switchImprimirAutomatico,
            abrirDrawerPagamento,
            drawerPagamentoAberto,
            fecharDrawerPagamento,
            pedidosEmAndamento,
            loadingPedidosEmAndamento,
            buscarPedidosEmAndamento,
            exibirNovoPedido,
            setExibirNovoPedido,
            cupons,
            aplicarCupom,
            removerCupom,
            drawerCuponsAberto,
            abrirDrawerCupons,
            fecharDrawerCupons,
            contatoChat,
            clienteIdentificado,
            chatUuid,
            loadingCliente,
            definirContatoChat,
            vincularClienteNoChat,
            desvincularClienteDoChat,
            cadastrarEVincularCliente,
            telaAtual,
            produtoFormulario,
            navegarParaCatalogo,
            navegarParaFormPizza,
            navegarParaFormPreparado,
            navegarParaFormPeso,
            voltarTela,
        }}>
            <EmitirNotaFiscalModal
                cancelar={handleModalNotaFiscal}
                concluir={fecharPedido}
                open={modalNotaFiscal}
                dadosCliente={cliente}
                loading={loading}
            />
            <DividirPagamento
                aberto={drawerPagamentoAberto}
                fechar={fecharDrawerPagamento}
                valorTotalPedido={valorTotalPedido}
                produtos={produtos}
                fecharPedido={fecharPedido}
                tipoPedido={tipo}
                valorFrete={valorFrete}
            />
            {children}
        </ChatPdVContext.Provider>
    );
};

